import { startOfMonth, startOfYesterday } from 'date-fns';
import type {
  ISnapshot,
  SnapshotList,
  SnapshotsSection,
  SnapshotsSections,
} from './SnapshotTypes';

const openedStates: ISnapshot['state'][] = [
  'discovering',
  'loading',
  'unloading',
];

export const sortSnapshotsByGroup = (
  snapshots: ISnapshot[],
  selectedSnapshot?: string[] | null,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: It is complex due to the nature of grouping snapshots
): SnapshotsSections => {
  const today: SnapshotsSection = {
    title: 'Today',
    opened: true,
    snapshots: [],
  };
  const yesterday: SnapshotsSection = {
    title: 'Yesterday',
    opened: true,
    snapshots: [],
  };
  const thisMonth: SnapshotsSection = {
    title: 'This month',
    opened: false,
    snapshots: [],
  };

  const monthSections: { [key: number]: SnapshotsSection } = {};

  const now = new Date();
  for (const snapshot of snapshots) {
    const snapshotDate = new Date(snapshot.tsStart ?? 0);
    if (snapshotDate.toDateString() === now.toDateString()) {
      today.snapshots.push(snapshot);
    } else if (snapshotDate.getTime() > startOfYesterday().getTime()) {
      yesterday.snapshots.push(snapshot);
    } else if (snapshotDate.getTime() > startOfMonth(new Date()).getTime()) {
      thisMonth.snapshots.push(snapshot);
      thisMonth.opened =
        selectedSnapshot?.includes(snapshot.id ?? '') || thisMonth.opened;
    } else {
      const monthKey = startOfMonth(snapshotDate).getTime();
      if (!monthSections[monthKey]) {
        monthSections[monthKey] = {
          title: new Date(monthKey).toLocaleString('en-US', {
            month: 'long',
            year: 'numeric',
          }),
          opened: false,
          snapshots: [],
        };
      }
      // Fixed noUncheckedIndexedAccess NIM-19408
      monthSections[monthKey]!.snapshots.push(snapshot);
      if (
        selectedSnapshot?.includes(snapshot.id ?? '') ||
        openedStates.includes(snapshot.state)
      ) {
        // Fixed noUncheckedIndexedAccess NIM-19408
        monthSections[monthKey]!.opened = true;
      }
    }
  }
  const sortedMonthSections = sortMothSections(monthSections);
  const sections = [today, yesterday, thisMonth, ...sortedMonthSections];

  return sections.filter((section) => section.snapshots.length > 0);
};

const sortMothSections = (other: {
  [key: number]: SnapshotsSection;
}): SnapshotsSection[] => {
  return Object.entries(other)
    .map(([_, section]) => section)
    .sort((a, b) => {
      const aDate = new Date(a.title).getTime();
      const bDate = new Date(b.title).getTime();
      return bDate - aDate; // Sort descending
    });
};

export const filterSnapshotsByName = (
  snapshots: ISnapshot[],
  searchValue: string,
  defaultName: string,
): ISnapshot[] => {
  if (!searchValue) {
    return snapshots;
  }
  const lowerCaseSearchValue = searchValue.toLowerCase();
  return snapshots.filter((snapshot) =>
    getSnapshotName(snapshot, defaultName)
      .toLowerCase()
      .includes(lowerCaseSearchValue),
  );
};

export const filterOnlyLoadedSnapshots = (
  snapshots: ISnapshot[],
): ISnapshot[] => {
  return snapshots.filter((snapshot) => snapshot.state !== 'unloaded');
};

export const concatSnapshots = (snapshotList: SnapshotList): ISnapshot[] => {
  const loadedSnapshots = snapshotList.loaded.children;
  const unloadedSnapshots = snapshotList.unloaded.children;

  return [...loadedSnapshots, ...unloadedSnapshots];
};

export const getSnapshotName = (
  snapshot: ISnapshot,
  defaultName: string,
): string => {
  const name = snapshot.name || defaultName;
  return snapshot.status === 'canceled' ? `(Canceled) ${name}` : name;
};

export const getSectionTitleBySnapshot = (
  sections: SnapshotsSections,
  snapshotId: string,
) => {
  for (const section of sections) {
    if (section.snapshots.some((snapshot) => snapshot.id === snapshotId)) {
      return section.title;
    }
  }
  return null;
};

export const sortSnapshotsByDate = (snapshots: ISnapshot[]): ISnapshot[] => {
  return snapshots.sort((a, b) => {
    const aDate = new Date(a.tsStart ?? 0).getTime();
    const bDate = new Date(b.tsStart ?? 0).getTime();
    return bDate - aDate; // Sort descending by start time
  });
};
